import ImmutablePropTypes from 'react-immutable-proptypes';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import {
  availabilityShape,
  catalogBibShape,
  libraryShape,
  listShape,
  userIdentityShape,
  paginationShape
} from '@bibliocommons/bc-prop-types';

import { trackAnalyticsEvent } from 'app/actions/AnalyticsGa4Actions';
import { getRelationships } from 'app/actions/RelationshipsActions';
import {
  eventNames,
  PROPERTY_CONTENT_COLLECTION_TYPE,
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TITLE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import mapListToAnalyticsListEntity from 'app/helpers/analytics/mapListToAnalyticsListEntity';
import {
  mapCatalogBibToDisplayBib,
  mapDisplayBibToAnalyticsBibEntity,
  mapSearchBibAvailabilityToDisplayAvailability
} from 'app/helpers/displayBib';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectAvailabilityEntities, selectUserEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import {
  selectCommunityListsForCurrentBib,
  selectCommunityListsPagination
} from 'app/selectors/bibs/DiscoverySelector';
import CommunityLists from './CommunityLists';

export function CommunityListsContainer(props) {
  const { availability, catalogBib, currentUser, lists } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    if (currentUser?.isEmpty() ?? true) {
      return;
    }

    const listOwnerIds = lists?.map(list => list.getIn(['owner', 'id']));

    dispatch(getRelationships({ userId: currentUser.get('id'), userIds: listOwnerIds }));
  }, [currentUser, dispatch, lists]);

  const trackListImageClick = listId => {
    const list = lists.find(nextList => nextList.get('id') === listId);
    const listEntity = mapListToAnalyticsListEntity(list);

    const uiEntity = {
      ui_container_type: UI_CONTAINER_TYPE.SECTION,
      ui_container_title: UI_CONTAINER_TITLE.COMMUNITY_LISTS,
      ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE
    };

    dispatch(
      trackAnalyticsEvent({
        entities: {
          list: listEntity,
          ui: uiEntity
        },
        eventName: eventNames.CONTENT_CLICK
      })
    );
  };

  const trackListTitleClick = (listId, listTitle) => {
    const list = lists.find(nextList => nextList.get('id') === listId);
    const listEntity = mapListToAnalyticsListEntity(list);

    const uiEntity = {
      ui_container_type: UI_CONTAINER_TYPE.SECTION,
      ui_container_title: UI_CONTAINER_TITLE.COMMUNITY_LISTS,
      ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
      ui_component_label: listTitle
    };

    dispatch(
      trackAnalyticsEvent({
        entities: {
          list: listEntity,
          ui: uiEntity
        },
        eventName: eventNames.CONTENT_CLICK
      })
    );
  };

  const trackAddToListClick = ({ uiEntity }) => {
    const bibEntity = mapDisplayBibToAnalyticsBibEntity(
      mapCatalogBibToDisplayBib(catalogBib.toJS()),
      mapSearchBibAvailabilityToDisplayAvailability(availability?.toJS())
    );

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: bibEntity,
          ui: uiEntity
        },
        eventName: eventNames.CONTENT_COLLECTION_ADD,
        properties: {
          content_collection_type: PROPERTY_CONTENT_COLLECTION_TYPE.LIST_TITLE
        }
      })
    );
  };

  const trackAddToNewListClick = ({ uiEntity }) => {
    const bibEntity = mapDisplayBibToAnalyticsBibEntity(
      mapCatalogBibToDisplayBib(catalogBib.toJS()),
      mapSearchBibAvailabilityToDisplayAvailability(availability?.toJS())
    );

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: bibEntity,
          ui: uiEntity
        },
        eventName: eventNames.USER_CONTENT_INITIATE
      })
    );
  };

  return (
    <CommunityLists
      {...props}
      trackAddToListClick={trackAddToListClick}
      trackAddToNewListClick={trackAddToNewListClick}
      trackListImageClick={trackListImageClick}
      trackListTitleClick={trackListTitleClick}
    />
  );
}

CommunityListsContainer.propTypes = {
  availability: availabilityShape,
  currentUser: userIdentityShape,
  library: libraryShape.isRequired,
  catalogBib: catalogBibShape.isRequired,
  pagination: paginationShape.isRequired,
  users: ImmutablePropTypes.mapOf(userIdentityShape),
  lists: ImmutablePropTypes.listOf(listShape).isRequired
};

const mapStateToProps = state => {
  const catalogBib = selectCatalogBib(state);

  const metadataId = catalogBib.get('metadataId');

  return {
    availability: selectAvailabilityEntities(state).get(metadataId),
    catalogBib,
    currentUser: selectCurrentUser(state),
    pagination: selectCommunityListsPagination(state),
    library: selectCurrentLibrary(state),
    lists: selectCommunityListsForCurrentBib(state),
    users: selectUserEntities(state)
  };
};

export default connect(mapStateToProps)(CommunityListsContainer);
